
import { Component, Vue, Watch } from 'vue-property-decorator';
import { DateTime } from 'luxon';

import { sharedState } from '../../../../framework/state';
import {
    getDatabaseOptions,
    getEnumerationOptions,
    getOptionLabel,
    OptionValue,
} from '../../../../framework/service/options';
import { deleteResource, getResourcePagingNew, getResourcesNew } from '../../../../framework/client/resource';
import { getResourceLabels } from '../../../../framework/service/label_service';

import SwitchField from '../../../../framework/fields/SwitchField.vue';
import DateField from '../../../../framework/fields/DateField.vue';
import MonthField from '../../../../framework/fields/MonthField.vue';

import { ApplicationResource } from '../../../../../common/application/enumeration/ApplicationResource';
import { SmartFleetTask } from '../../../../../common/application/model/SmartFleetTask';
import { Account } from '../../../../../common/application/model/Account';
import { VendorContractType } from '../../../../../common/application/model/enums/VendorContractType';

import ParameterService from '../../../service/parameter_service';
import AutoCompleteMultipleField from '../../../fields/AutoCompleteMultipleField.vue';
import ConfirmSendReminderModal from './ConfirmSendReminderModal.vue';
import ConfirmCompleteTaskModal from './ConfirmCompleteTaskModal.vue';
import { formatCurrency, formatToFinnishDate } from '../../../service/helper_service';
import { errorToast, successToast } from '../../../service/toast_service';
import { getCustomerOptions } from '../../../service/application_options_service';

@Component({
    components: {
        MonthField,
        DateField,
        ConfirmSendReminderModal,
        SwitchField,
        AutoCompleteMultipleField,
        ConfirmCompleteTaskModal,
    },
})
export default class SmartFleetTasks extends Vue {
    readonly resourceType = ApplicationResource.SMART_FLEET_TASK;
    parameterService = new ParameterService(this.resourceType);

    shared = sharedState;
    local = {
        rows: [] as SmartFleetTask[],
        selected: undefined as SmartFleetTask | undefined,
        vehicleContractLabels: new Map<string, string>(),
        loading: false,
        total: 0,
        smartFleetTaskTypeOptions: [] as OptionValue[],
        parameters: this.parameterService.get(),
        isConfirmSendModalActive: false,
        isConfirmCompleteModalActive: false,
        customerOptions: [] as OptionValue[],
        vendorContractTypeOptions: [] as OptionValue[],
        invoiceRowTypeOptions: [] as OptionValue[],
    };

    async mounted() {
        this.local.vendorContractTypeOptions = getEnumerationOptions(
            'VendorContractType',
            Object.keys(VendorContractType),
            this,
        );
        await this.loadAsyncData();
    }

    @Watch('local.parameters.completed')
    async completedFilterChanged() {
        if (this.local.loading) {
            return;
        }

        await this.resetPagingAndLoadData();
    }

    @Watch('local.parameters.adminOnly')
    async adminOnlyFilterChanged() {
        if (this.local.loading) {
            return;
        }

        await this.resetPagingAndLoadData();
    }

    async dropdownClosed(resourceIds: string[], idField: string): Promise<void> {
        if (this.local.loading) {
            return;
        }

        if (!resourceIds.length) {
            delete this.local.parameters[idField];
        } else {
            this.local.parameters[idField] = resourceIds;
        }

        await this.resetPagingAndLoadData();
    }

    async onPageChange(page: number) {
        this.local.parameters.page = page;
        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();
    }

    onRowClick(row: Account): void {
        if (this.local.selected && row.id === this.local.selected.id) {
            this.local.selected = undefined;
        }
    }

    async resetFilter(): Promise<void> {
        this.local.parameters = this.parameterService.getDefault();
        await this.resetPagingAndLoadData();
    }

    add() {
        this.$router.push(`/${this.resourceType}/add`);
    }

    edit(id?: string) {
        if (id) {
            this.$router.push(`/${this.resourceType}/${id}/edit`);
        } else if (this.local.selected) {
            this.$router.push(`/${this.resourceType}/${this.local.selected.id}/edit`);
        }
    }

    confirmDelete() {
        this.$buefy.dialog.confirm({
            title: this.$t('title.confirmComplete').toString(),
            message: this.$t('message.confirmComplete').toString(),
            cancelText: this.$t('button.cancel').toString(),
            confirmText: this.$t('button.ok').toString(),
            type: 'is-success',
            onConfirm: async () => {
                if (this.local.selected) {
                    try {
                        await deleteResource(this.resourceType, this.local.selected.id);
                        successToast(this, 'message.resourceDeleted');
                        await this.loadAsyncData();
                    } catch (e) {
                        errorToast(this);
                    }
                }
            },
        });
    }

    dateTimeToString(timestamp: string | undefined) {
        if (!timestamp) {
            return '';
        }

        return formatToFinnishDate(timestamp);
    }

    getSmartFleetTaskTypeLabel(id: string): string {
        return getOptionLabel(id, this.local.smartFleetTaskTypeOptions);
    }

    getVehicleContractLabel(id: string) {
        if (this.local.vehicleContractLabels.has(id)) {
            return this.local.vehicleContractLabels.get(id);
        } else {
            return id;
        }
    }

    getVendorContractTypeType(id: string): string {
        return getOptionLabel(id, this.local.vendorContractTypeOptions);
    }

    getInvoiceRowTypeLabel(id: string): string {
        return getOptionLabel(id, this.local.invoiceRowTypeOptions);
    }

    taskDeadlineInDays(deadline: string | undefined): string {
        if (!deadline) {
            return '';
        }

        return DateTime.fromJSDate(new Date(deadline)).diffNow('days').days.toFixed(0);
    }

    confirmMarkAsDone(smartFleetTask: SmartFleetTask) {
        this.local.selected = smartFleetTask;
        this.local.isConfirmCompleteModalActive = true;
    }

    completeTaskModalClosed(): void {
        this.local.isConfirmCompleteModalActive = false;
        this.local.selected = undefined;
        this.resetPagingAndLoadData().then();
    }

    async sendReminder(smartFleetTask: SmartFleetTask): Promise<void> {
        try {
            this.local.selected = smartFleetTask;
            this.local.isConfirmSendModalActive = true;
        } catch (error) {
            errorToast(this);
        }
    }

    get customerIds(): string[] {
        return this.local.parameters.customerId || [];
    }

    get isAdmin(): boolean {
        return this.shared.admin;
    }

    getCustomerName(id: string): string {
        return getOptionLabel(id, this.local.customerOptions);
    }

    getDynamicComment(smartFleetTask: SmartFleetTask): string {
        switch (smartFleetTask.comment) {
            case 'checkForUnpaidBilledCostsJob':
                return this.$t('message.checkForUnpaidBilledCostsJob', [
                    this.formatCurrencyValue(smartFleetTask.additionalInfo['payment']),
                    this.getVendorContractTypeType(smartFleetTask.additionalInfo['vendorContractType']),
                    smartFleetTask.additionalInfo['vendorName'],
                ]).toString();
            case 'monitorMonthlyCostsJob':
                return this.$t('message.monitorMonthlyCostsJob', [
                    smartFleetTask.additionalInfo['purchaseInvoiceNumber'],
                    this.getInvoiceRowTypeLabel(smartFleetTask.additionalInfo['invoiceRowType']),
                    this.formatCurrencyValue(smartFleetTask.additionalInfo['cumulativeSum']),
                ]).toString();
            case 'noVendorContract':
                return this.$t('message.noVendorContract', [
                    smartFleetTask.additionalInfo['purchaseInvoiceNumber'],
                ]).toString();
            default:
                return '-';
        }
    }

    formatCurrencyValue(value: number | null | undefined) {
        return formatCurrency(value);
    }

    private async loadAsyncData() {
        this.local.loading = true;
        this.local.rows = [];

        this.local.smartFleetTaskTypeOptions = await getDatabaseOptions('SmartFleetTaskType', this);
        this.local.invoiceRowTypeOptions = (await getDatabaseOptions('InvoiceRowType', this)).filter(
            ({ id }) => id !== undefined,
        );
        this.local.customerOptions = await getCustomerOptions();

        if (!this.isAdmin) {
            this.local.parameters.adminOnly = false;
        }

        const backendParameters = this.parameterService.getBackendParameters();
        this.local.total = (await getResourcePagingNew(this.resourceType, backendParameters)).rowCount;
        const rows = await getResourcesNew<SmartFleetTask>(
            this.resourceType,
            this.local.parameters.page,
            backendParameters,
        );

        rows.forEach((row) => {
            this.local.rows.push(row);
        });

        if (this.local && this.local.rows.indexOf(this.local.selected!!) == -1) {
            this.local.selected = undefined;
        }

        await this.getVehicleContractLabels();
        this.local.loading = false;
    }

    private async resetPagingAndLoadData() {
        this.local.parameters.page = 1;
        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();
    }

    private async getVehicleContractLabels(): Promise<void> {
        const results = await getResourceLabels(
            this.local.rows,
            'vehicleContractId',
            ApplicationResource.VEHICLE_CONTRACT,
            'vehicleRegistrationNumber',
        );
        this.local.vehicleContractLabels = new Map([...this.local.vehicleContractLabels, ...results]);
    }
}
